import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { ButtonContainer } from '../ShiftInformation.styles';
import { selectSelectedShift } from '../../../redux/shifts/shiftsSlice';
import { selectHasPrivilegedRole } from '../../../redux/slices/currentUserSlice';
import { selectAllEmployees } from '../../../redux/slices/employeesSlice';
import { selectShiftSwapLoading } from '../../../redux/shifts/shiftSwapSlice';
import { fetchEmployees } from '../../../redux/actions/employeesActions';
import { useEmployeeAssignment } from '../../../utils/Hooks/shifts/useEmployeeAssignment';
import { deleteShift } from '../../../redux/shifts/shiftsThunks';
import { cancelShiftSwapThunk } from '../../../redux/shifts/shiftSwapThunks';
import { clearShiftPendingSwap } from '../../../redux/shifts/shiftsSlice';
import { resetRequestStatus } from '../../../redux/shifts/shiftSwapSlice';
import { showSuccessToast, showErrorToast } from '../../../utils/toastUtils';
import ShiftSwapModal from './ShiftSwapModal';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';

/**
 * Component for displaying shift action buttons
 * Shows different actions based on user role:
 * - Managers: Unassign/Assign and Delete buttons
 * - Employees: Swap shift button
 *
 * @param {Object} props
 * @param {Function} props.handleCloseDrawer - Callback to close the drawer
 * @param {Function} props.handleBackNavigation - Callback to navigate back in drawer hierarchy
 * @param {Function} props.handleShiftUpdate - Function to update shift data
 * @param {Function} props.handleStartEmployeeSelection - Function to start employee selection
 * @param {boolean} props.disabled - Whether the actions are disabled
 * @param {boolean} props.shouldNavigateBackOnDelete - If true, navigates back one level on delete; otherwise closes drawer
 */
const ShiftActionsSection = ({
  handleCloseDrawer,
  handleBackNavigation,
  handleShiftUpdate,
  handleStartEmployeeSelection,
  disabled,
  shouldNavigateBackOnDelete = false,
}) => {
  const dispatch = useDispatch();
  const shift = useSelector(selectSelectedShift);
  const hasPrivilegedRole = useSelector(selectHasPrivilegedRole);
  const employees = useSelector(selectAllEmployees);
  const currentUser = useSelector((state) => state.currentUser);
  const isSwapLoading = useSelector(selectShiftSwapLoading);

  const [showShiftSwapModal, setShowShiftSwapModal] = useState(false);

  // Fetch employees when component mounts
  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  const shiftID = shift?.id;
  const isShiftAssigned = !!shift?.assignedName;
  const isShiftClockedIn = !!shift?.clockedInAt;
  const isCurrentUserAssignedToShift = shift?.eid === currentUser?.employeeInfo?.id;

  const { handleUnassignButtonClick } = useEmployeeAssignment(handleShiftUpdate);

  const handleDeleteButtonClick = async () => {
    try {
      await dispatch(deleteShift(shiftID)).unwrap();
      showSuccessToast('Shift deleted successfully');
      if (shouldNavigateBackOnDelete && handleBackNavigation) {
        handleBackNavigation();
      } else if (handleCloseDrawer) {
        handleCloseDrawer();
      }
    } catch (error) {
      console.error('Error deleting shift:', error);
      showErrorToast('Failed to delete shift');
    }
  };

  const handleOpenShiftSwapModal = () => {
    setShowShiftSwapModal(true);
  };

  const handleCloseShiftSwapModal = () => {
    setShowShiftSwapModal(false);
  };

  const handleCancelSwapRequest = async () => {
    if (!shift?.pendingSwap) return;

    try {
      // If it's an optimistic update, just clear it locally
      if (shift.pendingSwap === 'pending') {
        dispatch(clearShiftPendingSwap({ shiftId: shift.id }));
        dispatch(resetRequestStatus());
        showSuccessToast('Swap request canceled');
      } else {
        // Cancel the actual request on the server
        await dispatch(cancelShiftSwapThunk({ shiftSwapRequestId: shift.pendingSwap })).unwrap();
        dispatch(clearShiftPendingSwap({ shiftId: shift.id }));
        dispatch(resetRequestStatus());
        showSuccessToast('Swap request canceled successfully');
      }
    } catch (error) {
      showErrorToast(`Failed to cancel swap request: ${error}`);
    }
  };

  // Show different actions based on user role
  if (hasPrivilegedRole) {
    // Manager/Admin actions
    return (
      <ButtonContainer>
        {isShiftAssigned ? (
          <Button variant="secondaryDestructive" size="medium" onClick={handleUnassignButtonClick} disabled={disabled}>
            Unassign shift
          </Button>
        ) : (
          <Button variant="primary" size="medium" onClick={handleStartEmployeeSelection} disabled={disabled}>
            Assign shift
          </Button>
        )}
        <Button
          variant="primaryDestructive"
          size="medium"
          onClick={handleDeleteButtonClick}
          disabled={isShiftClockedIn}
        >
          Delete shift
        </Button>
      </ButtonContainer>
    );
  } else if (isCurrentUserAssignedToShift) {
    // Employee actions - only show if they are assigned to this shift
    const hasPendingSwap = !!shift?.pendingSwap;

    return (
      <>
        <ButtonContainer>
          {hasPendingSwap ? (
            <Button
              variant="primaryDestructive"
              onClick={handleCancelSwapRequest}
              disabled={disabled || isShiftClockedIn || isSwapLoading}
            >
              {isSwapLoading ? <CircularProgress size={20} color="inherit" /> : 'Cancel swap request'}
            </Button>
          ) : (
            <Button variant="primary" onClick={handleOpenShiftSwapModal} disabled={disabled || isShiftClockedIn}>
              Request shift swap
            </Button>
          )}
        </ButtonContainer>
        <ShiftSwapModal
          open={showShiftSwapModal}
          onClose={handleCloseShiftSwapModal}
          shift={shift}
          employees={employees}
        />
      </>
    );
  }

  // If user has no privileges and is not assigned to shift, show nothing
  return null;
};

ShiftActionsSection.propTypes = {
  handleCloseDrawer: PropTypes.func.isRequired,
  handleBackNavigation: PropTypes.func.isRequired,
  handleShiftUpdate: PropTypes.func.isRequired,
  handleStartEmployeeSelection: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  shouldNavigateBackOnDelete: PropTypes.bool,
};

export default ShiftActionsSection;
